import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import Seo from "@/components/Seo";
import { FadeUp } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function renderBlocks(body) {
  const lines = String(body || "").split("\n");
  const blocks = [];
  let list = [];
  const flush = () => {
    if (list.length) {
      blocks.push({ type: "ul", items: list });
      list = [];
    }
  };
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flush();
      continue;
    }
    if (line.startsWith("- ")) {
      list.push(line.slice(2));
      continue;
    }
    flush();
    if (line.startsWith("## ")) blocks.push({ type: "h2", text: line.slice(3) });
    else blocks.push({ type: "p", text: line });
  }
  flush();
  return blocks;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    axios
      .get(`${API}/blog/${encodeURIComponent(slug)}`)
      .then(({ data }) => setPost(data))
      .catch((e) => {
        if (e.response && e.response.status === 404) setNotFound(true);
        else setNotFound(true);
      });
  }, [slug]);

  if (notFound) {
    return (
      <main
        data-testid="blog-post-missing"
        className="flex min-h-screen flex-col items-center justify-center gap-6 bg-obsidian px-6 text-center"
      >
        <h1 className="font-display text-3xl font-bold text-slate-900">
          That post isn't here.
        </h1>
        <p className="max-w-md text-base text-mist">
          It may have been removed or renamed — the rest of Todd's tips are on
          the blog.
        </p>
        <Link
          to="/blog"
          data-testid="blog-post-back-link"
          className="rounded-2xl border border-slate-300 px-6 py-3 font-mono text-base font-semibold uppercase tracking-[0.15em] text-slate-900 transition-colors hover:border-flame/60 hover:text-flame"
        >
          Back to the blog
        </Link>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="min-h-screen bg-obsidian px-6 pt-40 text-base text-faint">
        Loading…
      </main>
    );
  }

  const blocks = renderBlocks(post.body);

  return (
    <main data-testid="blog-post-page">
      <Seo
        title={`${post.title} — ZD Design`}
        description={post.meta_description || post.excerpt}
        path={`/blog/${post.slug}`}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            datePublished: post.created_at,
            author: { "@type": "Person", name: "Todd Meisler" },
            publisher: { "@type": "Organization", name: "ZD Design Agency" },
          })}
        </script>
      </Helmet>

      <article className="grain relative overflow-hidden pb-20 pt-40 md:pt-48">
        <div className="pointer-events-none absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-flame/10 blur-[140px]" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-8">
          <FadeUp>
            <Link
              to="/blog"
              data-testid="blog-post-to-blog-link"
              className="inline-flex items-center gap-2 font-mono text-base uppercase tracking-[0.2em] text-faint transition-colors hover:text-flame"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              All tips
            </Link>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
              {post.title}
            </h1>
            <div className="mt-5 font-mono text-base uppercase tracking-[0.2em] text-faint">
              {String(post.created_at || "").slice(0, 10)} · By Todd Meisler
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div
              data-testid="blog-post-body"
              className="mt-10 space-y-5 border-t border-slate-200 pt-10 text-lg leading-relaxed text-slate-800"
            >
              {blocks.map((b, i) =>
                b.type === "h2" ? (
                  <h2
                    key={i}
                    className="pt-4 font-display text-2xl font-bold tracking-tight text-slate-900"
                  >
                    {b.text}
                  </h2>
                ) : b.type === "ul" ? (
                  <ul key={i} className="space-y-2">
                    {b.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-flame" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p key={i}>{b.text}</p>
                )
              )}
            </div>
          </FadeUp>
        </div>
      </article>
    </main>
  );
}
